import { ChevronDown, Info } from 'lucide-react';
import { useState } from 'react';

import { COLORS } from './constants';
import { NHDHeroCardProps, NHDHeroContent } from './NHDHeroCard';
import { Tip } from './Tip';

import { NHDFinding, NHDTaxAssessments } from '@/types/NHD';

/** Definitions for statutory terms surfaced inside the disclosures
 *  accordion. Keep prose short — these render as 1-2 sentence tooltips. */
const TERM_TOOLTIPS: Record<string, string> = {
  // Disclosure labels (match the `label` field on stub findings)
  'FEMA Special Flood Hazard Area':
    'A federally designated area at high risk of flooding. ' +
    'Mortgage lenders typically require flood insurance for properties inside this zone.',
  'Earthquake Fault Zone (Alquist-Priolo)':
    'A state-designated zone within ~50 ft of an active earthquake fault. New habitable structures generally cannot be built across the trace.',
  'Seismic Hazard Zone':
    'A state-designated zone where ground shaking, liquefaction, or earthquake-induced ' +
    'landslides are likely. Site-specific geotechnical investigation may be required ' +
    'for new construction.',
  'Airport Influence Area':
    'An area within range of regular aircraft noise or overflight. Triggers additional disclosure requirements at sale.',
  'Former Military Ordnance Site':
    'An area within 1 mile of a former federal/state military training, maneuver, or ordnance site. Unexploded ordnance contamination may be possible.',
  'Commercial / Industrial Zone':
    'Property is within or adjacent to land zoned for commercial or industrial use. May affect noise, traffic, or future development character.',
  'Right to Farm Act':
    'County has a Right-to-Farm ordinance. Adjacent agricultural operations may produce ' +
    'noise, dust, odors, and chemical applications that cannot be challenged as nuisances.',
  'Mello-Roos Special Tax District':
    'Special tax district (Community Facilities District) levying additional property tax to fund infrastructure or services. Tax may run for 20–40 years.',
  // Tax terms
  'Annual Tax':
    "The total annual property tax liability — ad valorem (1% of assessed value) plus voter-approved bonds, special assessments, and direct charges.",
  'Ad Valorem':
    "The base property tax — 1% of the assessed value plus voter-approved overrides (school bonds, etc.). Capped under Prop 13 at the assessed value's 2% annual growth.",
  'Direct Assessments':
    "Fixed annual charges from special districts (water, sewer, schools, infrastructure bonds) levied on top of ad-valorem property tax. Not capped by Prop 13.",
  // Tax flag chips
  'Mello-Roos':
    'Property is within a Community Facilities District (CFD) levying special tax to fund infrastructure or services. Tax typically runs 20–40 years.',
  'PACE Contract':
    'Property has an active Property Assessed Clean Energy financing — repaid via the property-tax bill. Transfers with the property at sale.',
  '1915 Bond':
    'Improvement bond from a special assessment district (1915 Act). Pays for street, sewer, or other infrastructure improvements over a 20–30 year term.',
};

export interface NHDDisclosuresCardProps {
  /** Pure binary findings — no scoring impact, just disclosure listing. */
  disclosures: NHDFinding[];
  /** Tax / Mello-Roos / PACE / direct-assessment data. Disclosure-only;
   *  rendered as a "Tax Profile" sub-section inside the same accordion. */
  taxAssessments?: NHDTaxAssessments;
  /** Optional source label, e.g. "JCP-LGS". */
  source?: string;
  /** ISO timestamp of when the report was extracted. Rendered next to
   *  the source as a human-readable date. */
  extractedAt?: string;
  /** Whether the accordion starts open. Defaults false (matches the
   *  pillar accordion behavior on ContextScoreView). */
  defaultOpen?: boolean;
  /** Single dominant NHD finding (e.g. Very High Fire Hazard) surfaced as
   *  an always-visible highlight above the disclosures accordion, so the
   *  headline finding and the full disclosure list read as one card. */
  heroFinding?: NHDHeroCardProps['finding'];
  /** Optional explicit icon override for heroFinding. */
  heroIcon?: NHDHeroCardProps['icon'];
}

const fmtMoney = (n: number | undefined): string => {
  if (n == null) return '—';
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

const formatExtractedAt = (iso: string | undefined): string => {
  if (!iso) return 'date unknown';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'date unknown';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const hasTaxData = (t?: NHDTaxAssessments): boolean => {
  if (!t) return false;
  return Boolean(
    t.melloRoos ||
      t.paceContract ||
      t.bond1915 ||
      (t.directAssessmentsTotal && t.directAssessmentsTotal > 0) ||
      (t.totalAnnualTaxLiability && t.totalAnnualTaxLiability > 0),
  );
};

/**
 * Renders the binary NHD disclosures as a checklist with In/Out
 * indicators. Mirrors the pillar-card visual language used elsewhere on
 * the Context Score view so users read it as "another category" rather
 * than a foreign block. Findings that flip to inZone=true get a small
 * NHD-tinted highlight; findings that are not in zone read as confirmed
 * negatives (which is itself useful information for buyers).
 */
export const NHDDisclosuresCard = ({
  disclosures,
  taxAssessments,
  source,
  extractedAt,
  defaultOpen = false,
  heroFinding,
  heroIcon,
}: NHDDisclosuresCardProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const showTax = hasTaxData(taxAssessments);
  if (!disclosures.length && !showTax && !heroFinding) return null;
  const inZoneCount = disclosures.filter((d) => d.inZone).length;
  const itemCount = disclosures.length + (showTax ? 1 : 0);
  return (
    <div
      style={{
        background: COLORS.card,
        borderRadius: 25,
        backgroundImage: 'var(--tx-surface-light)',
        boxShadow: 'var(--tx-shadow)',
        overflow: 'hidden',
      }}>
      {/* Always-visible highlight — the single most-impactful NHD finding
          (e.g. Very High Fire Hazard), folded in above the accordion so
          it reads as one card instead of a stacked pair. */}
      {heroFinding && (
        <div style={{ padding: '20px 20px 18px' }}>
          <NHDHeroContent finding={heroFinding} icon={heroIcon} />
        </div>
      )}
      {heroFinding && (disclosures.length > 0 || showTax) && (
        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '0 20px' }} />
      )}
      {(disclosures.length > 0 || showTax) && (
        <>
          {/* Accordion header — matches the pillar accordion visual on
              ContextScoreView (fixed 50px height, chevron on right, click
              anywhere to toggle). */}
          <div
            onClick={() => setIsOpen((v) => !v)}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0 20px',
              height: 50,
              cursor: 'pointer',
              gap: 10,
            }}>
            <span style={{ fontSize: 14, fontWeight: 300, color: COLORS.text }}>
              NHD &amp; Other Disclosures
            </span>
            <span style={{ fontSize: 14, color: 'var(--tx-muted)', whiteSpace: 'nowrap' }}>
              ({itemCount})
            </span>
            <div style={{ flex: 1 }} />
            {/* NHD count badge — right-aligned alongside the chevron so it
                tracks visually with the In/Not-In statuses inside the rows. */}
            {inZoneCount > 0 && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: COLORS.nhdAccent,
                  background: 'var(--tx-fill-2)',
                  padding: '2px 8px',
                  borderRadius: 10,
                }}>
                {inZoneCount} in zone
              </span>
            )}
            <span
              style={{
                color: COLORS.textDim,
                display: 'inline-flex',
                alignItems: 'center',
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.18s ease',
              }}>
              <ChevronDown size={14} />
            </span>
          </div>

          {/* Body */}
          {isOpen && (
            <div
              style={{
                padding: '0 20px 20px',
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
              }}>
              <div
                style={{
                  fontSize: 11,
                  color: COLORS.textDim,
                  lineHeight: 1.55,
                }}>
                Statutory disclosures from the NHD report. These do not affect the score —
                they exist to make sure you know what comes with the address.
              </div>

              {/* Binary disclosure list */}
              {disclosures.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {disclosures.map((d, i) => (
                    <DisclosureRow key={`${d.label}-${i}`} finding={d} />
                  ))}
                </div>
              )}

              {/* Tax Profile sub-section */}
              {showTax && taxAssessments && (
                <TaxProfileSection tax={taxAssessments} hasDivider={disclosures.length > 0} />
              )}

              {/* Source — always visible at the bottom; shows the document
                  and extraction date so users see what NHD report drives
                  everything above. */}
              <div
                style={{
                  fontSize: 10,
                  color: COLORS.subtle ?? COLORS.textDim,
                  opacity: 0.7,
                  marginTop: 4,
                }}>
                Source: {source ?? 'NHD report'} · {formatExtractedAt(extractedAt)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

function DisclosureRow({ finding }: { finding: NHDFinding }) {
  const tone = finding.inZone ? COLORS.nhdAccent : COLORS.textDim;
  const bg = finding.inZone ? 'rgba(187,183,175,0.10)' : 'transparent';
  return (
    <div
      style={{
        padding: '10px 14px',
        background: bg,
        border: 0,
        borderRadius: 10,
      }}>
      {/* First-line row: checkbox + label + In/Not-In status, all
          vertically centered with each other so the box optically aligns
          with the text glyph regardless of internal-leading. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
        {/* In-zone indicator — filled circle + checkmark. Not-in-zone
            items render nothing (the spacer keeps labels aligned in a
            column so the eye can scan vertically). The empty checkbox
            was being misread as "needs checking" so we ditched it. */}
        <div
          style={{
            width: 18,
            height: 18,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
          {finding.inZone && <Info size={16} color={COLORS.nhdAccent} strokeWidth={1.75} aria-hidden />}
        </div>

        <Tip
          text={TERM_TOOLTIPS[finding.label]}
          placement="top"
          style={{
            flex: 1,
            minWidth: 0,
            borderBottom: 'none',
            // Match the inner span's line-height so the Tip wrapper's
            // line-box hugs the glyph instead of baseline-pushing it.
            lineHeight: 1,
            display: 'flex',
            alignItems: 'center',
          }}>
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: COLORS.text,
              lineHeight: 1,
            }}>
            {finding.label}
          </span>
        </Tip>
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: tone,
            whiteSpace: 'nowrap',
            textAlign: 'right',
            flexShrink: 0,
            lineHeight: 1,
          }}>
          {finding.zone ? `${finding.zone} · ` : ''}
          {finding.inZone ? 'In zone' : 'Not in zone'}
        </span>
      </div>

      {/* Detail paragraph — indented to align with the label column
          (checkbox 18 + gap 12 = 30px). */}
      <div style={{ paddingLeft: 30 }}>
        {finding.detail && (
          <div
            style={{
              marginTop: 4,
              fontSize: 11,
              color: COLORS.textDim,
              lineHeight: 1.55,
            }}>
            {finding.detail}
          </div>
        )}
      </div>
    </div>
  );
}

function TaxProfileSection({
  tax,
  hasDivider = true,
}: {
  tax: NHDTaxAssessments;
  hasDivider?: boolean;
}) {
  const flags: Array<{ label: string; on: boolean }> = [
    { label: 'Mello-Roos', on: !!tax.melloRoos },
    { label: 'PACE Contract', on: !!tax.paceContract },
    { label: '1915 Bond', on: !!tax.bond1915 },
  ];
  return (
    <div
      style={{
        marginTop: hasDivider ? 4 : 0,
        paddingTop: hasDivider ? 14 : 0,
        borderTop: hasDivider ? `1px solid ${COLORS.border}` : 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: COLORS.textDim,
          }}>
          Market · Tax Profile
        </span>
        {tax.taxYear && (
          <span style={{ fontSize: 10, color: COLORS.textDim, opacity: 0.7 }}>
            {tax.taxYear}
          </span>
        )}
      </div>

      {/* Top-level totals — three columns on desktop, stacked on
          narrow viewports so the stat values don't truncate. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: 10,
        }}>
        <TaxStat
          label="Annual tax"
          value={fmtMoney(tax.totalAnnualTaxLiability)}
          tooltip={TERM_TOOLTIPS['Annual Tax']}
        />
        <TaxStat
          label="Ad valorem"
          value={fmtMoney(tax.adValoremTotal)}
          tooltip={TERM_TOOLTIPS['Ad Valorem']}
        />
        <TaxStat
          label="Direct assessments"
          value={fmtMoney(tax.directAssessmentsTotal)}
          tooltip={TERM_TOOLTIPS['Direct Assessments']}
        />
      </div>

      {/* Flag chips — each chip carries a tooltip defining the term */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {flags.map((f) => (
          <Tip
            key={f.label}
            text={TERM_TOOLTIPS[f.label]}
            placement="top"
            style={{ borderBottom: 'none', display: 'inline-flex' }}>
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.04em',
                padding: '4px 10px',
                borderRadius: 6,
                color: f.on ? COLORS.nhdAccent : COLORS.textDim,
                background: f.on ? 'var(--tx-fill-2)' : 'var(--tx-fill)',
                border: 0,
              }}>
              {f.label}: {f.on ? 'YES' : 'NO'}
            </span>
          </Tip>
        ))}
      </div>

      {/* Direct assessment line items */}
      {tax.directAssessments && tax.directAssessments.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 10, color: COLORS.textDim, opacity: 0.75 }}>
            Direct assessment line items
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {tax.directAssessments.map((line, i) => (
              <div
                key={`${line.agency}-${i}`}
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 8,
                  fontSize: 11,
                  color: COLORS.text,
                  padding: '4px 0',
                }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: 600 }}>{line.agency}</span>
                  <span style={{ color: COLORS.textDim, marginLeft: 6 }}>
                    {line.description}
                  </span>
                </span>
                <span style={{ fontVariantNumeric: 'tabular-nums', color: COLORS.text }}>
                  {fmtMoney(line.amount)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div
        style={{
          fontSize: 10,
          color: COLORS.textDim,
          opacity: 0.6,
          fontStyle: 'italic',
        }}>
        Tax obligations are statutory, not condition-based. Disclosed for visibility;
        not factored into the score.
      </div>
    </div>
  );
}

function TaxStat({
  label,
  value,
  tooltip,
}: {
  label: string;
  value: string;
  tooltip?: string;
}) {
  const labelEl = (
    <div
      style={{
        fontSize: 10,
        fontWeight: 600,
        color: COLORS.textDim,
        marginBottom: 3,
      }}>
      {label}
    </div>
  );
  return (
    <div
      style={{
        background: 'rgba(var(--tx-ink-rgb), 0.05)',
        borderRadius: '9px',
        padding: '8px 12px',
      }}>
      {tooltip ? (
        <Tip text={tooltip} placement="top" style={{ borderBottom: 'none' }}>
          {labelEl}
        </Tip>
      ) : (
        labelEl
      )}
      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: COLORS.text,
          fontVariantNumeric: 'tabular-nums',
        }}>
        {value}
      </div>
    </div>
  );
}

export default NHDDisclosuresCard;
